import React from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useRealtime, type PresenceUser } from "./RealtimeProvider";
import { UserAvatar } from "../Components/UserAvatar";
import { RoleBadgeList, type RoleBadgeInfo } from "../Components/RoleBadges";
import { useUserDirectory } from "../Components/Account/useUserDirectory";
import { usePermissions } from "../Auth/PermissionsContext";

/**
 * Présence dans la barre supérieure : la pile des AUTRES admins connectés, puis sa propre
 * photo tout à droite ; l'ANNEAU VERT autour de sa photo est l'indicateur « en ligne ».
 * Les BADGES de rôle (dominance calculée serveur) s'affichent en format optimisé : le badge
 * PRINCIPAL du compte connecté à côté de sa photo (couronne = Responsable), et pour les autres
 * un popover au survol de l'avatar (nom + badges complets) pour ne pas charger la barre.
 */

const PresenceAvatar: React.FC<{ user: PresenceUser; badges: RoleBadgeInfo[] }> = ({ user, badges }) => {
	const inner = (
		<span className="group relative inline-flex">
			<UserAvatar
				name={user.user}
				id={user.userId ?? undefined}
				avatarKey={user.avatarKey}
				pictureUrl={user.pictureUrl}
				ringClass="ring-2 ring-white"
			/>
			{/* Popover au survol : nom + badges complets (jamais dans la barre elle-même). */}
			<span className="pointer-events-none absolute right-0 top-full z-30 mt-1.5 hidden w-max max-w-[240px] flex-col gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2 shadow-lg group-hover:flex">
				<span className="text-[11px] font-semibold text-slate-700">
					{user.user}
					{user.pages?.length ? ` (${user.pages.length} page${user.pages.length > 1 ? "s" : ""})` : ""}
				</span>
				{badges.length > 0 && <RoleBadgeList badges={badges} max={4} />}
			</span>
		</span>
	);
	return user.userId ? (
		<Link to={`/account/${user.userId}`} className="transition hover:z-10 hover:opacity-90" aria-label={`Profil de ${user.user}`}>
			{inner}
		</Link>
	) : (
		<span>{inner}</span>
	);
};

export const PresenceBar: React.FC = () => {
	const { connected, users } = useRealtime();
	const { user: authUser } = useAuth0();
	const { badges: ownBadges } = usePermissions();
	const { resolveBadges } = useUserDirectory();
	const ownEmail = (authUser?.email ?? "").trim().toLowerCase();
	const isSelf = (u: PresenceUser) => !!ownEmail && (u.email ?? "").trim().toLowerCase() === ownEmail;
	const self = users.find(isSelf) ?? null;
	const others = users.filter((u) => !isSelf(u));

	const MAX = 4;
	const shown = others.slice(0, MAX);
	const overflow = Math.max(0, others.length - MAX);
	const ownTitle = connected ? "En ligne" : "Hors ligne";

	return (
		<div className="flex items-center gap-2">
			{shown.length > 0 && (
				<div className="flex -space-x-2">
					{shown.map((u, i) => (
						<PresenceAvatar
							key={`${u.userId ?? u.user}-${i}`}
							user={u}
							badges={(resolveBadges(u.email ?? u.user) as RoleBadgeInfo[]) ?? []}
						/>
					))}
					{overflow > 0 && (
						<span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-500 ring-2 ring-white">
							+{overflow}
						</span>
					)}
				</div>
			)}
			{/* Sa propre photo, cercle seul (les badges vivent dans la carte Équipe et au survol) ;
			    l'anneau vert = connecté. */}
			<Link to="/account" className="group relative flex items-center gap-1.5 transition hover:opacity-90" aria-label="Mon compte" title={ownTitle}>
				<UserAvatar
					name={self?.user ?? authUser?.name ?? "Moi"}
					id={self?.userId ?? undefined}
					avatarKey={self?.avatarKey}
					pictureUrl={self?.pictureUrl ?? authUser?.picture ?? null}
					ringClass={connected ? "ring-2 ring-emerald-500" : "ring-2 ring-slate-300"}
					title={ownTitle}
				/>
				{ownBadges.length > 1 && (
					<span className="pointer-events-none absolute right-0 top-full z-30 mt-1.5 hidden w-max max-w-[240px] rounded-lg border border-slate-200 bg-white px-2.5 py-2 shadow-lg group-hover:block">
						<RoleBadgeList badges={ownBadges as RoleBadgeInfo[]} max={5} />
					</span>
				)}
			</Link>
		</div>
	);
};

export default PresenceBar;
